import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import NotInterested from '@material-ui/icons/NotInterested';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';

import history from 'services/history';

import { removeAllPinnedPages } from 'actions/pinnedPages';

// app: components
import ConfirmDeletionDialog from 'components/ConfirmDeletionDialog';
import Icon from 'components/Icon';
import Link from 'components/Link';

import { COLOURS } from 'constants/index';

import assets from 'services/assets';
import { getImageSrc, getImageSrcSet } from 'services/image';

import CssPinnedPages from './PinnedPages.scss';

const PinnedPages = forwardRef((props, ref) => {
  const { className, pinnedPages, isPinnedPagesOpen, togglePinnedPages } = props;
  useStyles(CssPinnedPages);
  const containerRef = useRef(null);
  const linkRef = useRef(null);

  const dispatch = useDispatch();

  const [confirmCleanUpDialogOpen, setConfirmCleanUpDialogOpen] = useState(false);
  const [cachedRecipesArray, setCachedRecipesArray] = useState([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const platformId = useMemo(
    () => (typeof window !== 'undefined' && window.cordova ? window.cordova.platformId : 'web'),
    [],
  );

  const pagesArray = useMemo(
    () =>
      Object.keys(pinnedPages).map(key => ({
        pathname: key,
        ...pinnedPages[key],
      })),
    [pinnedPages],
  );

  const aPageIsActive = useMemo(
    () => pagesArray.some(page => history.location.pathname.includes(page.pathname)),
    [pagesArray, history.location.pathname],
  );

  const showPinnedPages = useCallback(
    () => {
      if (isPinnedPagesOpen) {
        togglePinnedPages(null);

        return;
      }

      togglePinnedPages();
    },
    [isPinnedPagesOpen, togglePinnedPages],
  );

  const checkScrollability = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollWidth - container.clientWidth > container.scrollLeft);
    }
  };

  const getLinkRef = () => {
    const linkElement = document.querySelector('.pinned-pages__link');

    if (!linkElement) {
      return;
    }

    linkRef.current = linkElement;
  };

  useEffect(
    () => {
      checkScrollability();
      getLinkRef();

      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScrollability);
        window.addEventListener('resize', checkScrollability);
      }

      return () => {
        if (container) {
          container.removeEventListener('scroll', checkScrollability);
          window.removeEventListener('resize', checkScrollability);
        }
      };
    },
    [pagesArray],
  );

  useEffect(
    () => {
      if (pagesArray.length === cachedRecipesArray.length) {
        return;
      }

      setCachedRecipesArray(pagesArray);

      if (containerRef.current) {
        containerRef.current.scrollLeft = containerRef.current.scrollWidth;
      }

      showPinnedPages();
    },
    [pagesArray],
  );

  useEffect(
    () => {
      const activeLink = containerRef.current?.querySelector('[data-is-active="true"]');

      if (activeLink && containerRef.current) {
        // Calculate the scroll position to center the active link
        const container = containerRef.current;
        const activeLinkRect = activeLink.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const scrollLeft = activeLink.offsetLeft - (containerRect.width - activeLinkRect.width) / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    },
    [aPageIsActive],
  );

  if (!pagesArray.length) {
    return null;
  }

  const handleCancelDelete = () => {
    setConfirmCleanUpDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    dispatch(removeAllPinnedPages());
    setConfirmCleanUpDialogOpen(false);
  };

  const handleCleanUpClick = () => {
    setConfirmCleanUpDialogOpen(true);
  };

  const handleScrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: -300, // You can adjust this value based on your needs
        behavior: 'smooth',
      });
    }
  };

  const handleScrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: 300, // You can adjust this value based on your needs
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <div
        ref={ref}
        className={classNames('pinned-pages__container', {
          'pinned-pages__container--open': isPinnedPagesOpen,
          'pinned-pages__container--closed': !isPinnedPagesOpen,
        })}
      >
        <div
          className={classNames('pinned-pages', className, {
            'pinned-pages__focused': aPageIsActive,
          })}
          ref={containerRef}
        >
          {pagesArray.map(({ pathname, ...page }) => {
            const hasPrimaryImage =
              page.primary_image &&
              page.primary_image.crops &&
              page.primary_image.crops.length &&
              page.primary_image.crops[0].sizes.length;

            let preferredCropType = 'square';

            if (hasPrimaryImage && !page.primary_image.crops.find(crop => crop.type === 'square')) {
              preferredCropType = 'original';
            }

            const imageSrc = hasPrimaryImage
              ? getImageSrc(
                  page.primary_image,
                  preferredCropType,
                  size => size.w >= 315 || size.h >= 315,
                )
              : assets.coverUrl(page.book ? page.book.image_name : '', {
                  resize: 'fit-in',
                  width: 100,
                  height: 100,
                });

            const imageSrcSet = hasPrimaryImage
              ? getImageSrcSet(
                  page.primary_image,
                  preferredCropType,
                  size => size.w >= 100 && size.w <= 640,
                )
              : undefined;

            return (
              <Link
                key={pathname}
                to={pathname}
                disabled={!isPinnedPagesOpen}
                data-is-active={history.location.pathname.includes(pathname)}
                className={classNames('pinned-pages__link')}
              >
                <div className="pinned-pages_image">
                  <img
                    loading="lazy"
                    className={`pinned-pages_image${hasPrimaryImage ? '__primary' : '__book'}`}
                    src={imageSrc}
                    srcSet={imageSrcSet}
                    alt={page.title}
                    style={{ objectFit: hasPrimaryImage ? 'cover' : 'contain' }}
                  />
                </div>
                <h2
                  className="pinned-pages__title pinned-pages__title-lines-2"
                  style={{ webkitBoxOrient: 'vertical' }}
                >
                  {page.title}
                </h2>
                <Icon
                  className="pinned-pages__link_pin-icon"
                  fill={COLOURS.ALABASTER}
                  name="push-pin-filled"
                  height="30"
                  width="30"
                />
                <Icon
                  className="pinned-pages__link_pin-icon__bigger"
                  fill={COLOURS.ALABASTER}
                  name="push-pin-filled"
                  height="36"
                  width="36"
                />
              </Link>
            );
          })}
          <Button
            color="primary"
            size="medium"
            disabled={!isPinnedPagesOpen}
            onClick={handleCleanUpClick}
            title="Remove all pins"
            className="pinned-pages__cleanup-card"
            style={
              linkRef.current && isPinnedPagesOpen
                ? {
                    height: linkRef.current.offsetHeight,
                    width: linkRef.current.offsetWidth,
                  }
                : undefined
            }
            aria-label="Remove all pins"
          >
            <NotInterested fontSize="large" />
          </Button>
        </div>

        {platformId === 'web' &&
          canScrollLeft &&
          isPinnedPagesOpen && (
            <Button
              onClick={handleScrollLeft}
              className="pinned-pages__scroll_button pinned-pages__scroll_left"
            >
              <ChevronLeft fontSize="medium" />
            </Button>
          )}

        {platformId === 'web' &&
          canScrollRight &&
          isPinnedPagesOpen && (
            <Button
              onClick={handleScrollRight}
              className="pinned-pages__scroll_button pinned-pages__scroll_right"
            >
              <ChevronRight fontSize="medium" />
            </Button>
          )}
      </div>

      <ConfirmDeletionDialog
        title="Are you sure you want to unpin all the pinned recipes?"
        body=""
        proceedText="Unpin all"
        open={confirmCleanUpDialogOpen}
        onClose={handleCancelDelete}
        onAgree={handleConfirmDelete}
      />
    </>
  );
});

PinnedPages.propTypes = {
  className: PropTypes.string,
  pinnedPages: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string,
      book: PropTypes.shape({
        image_name: PropTypes.string,
      }),
      primary_image: PropTypes.shape({
        crops: PropTypes.arrayOf(
          PropTypes.shape({
            sizes: PropTypes.arrayOf(
              PropTypes.shape({
                src: PropTypes.string,
              }),
            ),
          }),
        ),
      }),
    }),
  ),
  isPinnedPagesOpen: PropTypes.bool,
  togglePinnedPages: PropTypes.func,
};

PinnedPages.defaultProps = {
  className: '',
  pinnedPages: {},
  isPinnedPagesOpen: false,
  togglePinnedPages: () => {},
};

export default React.memo(PinnedPages);
